import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from './status';
import type { ShipNodeStation, ShipNodeStationsService } from './types';

export const STATION_REFRESH_MS = 15_000;

type State = {
  stations: ShipNodeStation[];
  loading: boolean;
  error: string | null;
};

/**
 * The stations `service` lists, refreshed every 15 s while the browser tab is visible. A hidden tab stops
 * polling and catches up the moment it's shown. A new `service` (the WMS switching warehouse) reloads at once
 * and drops the previous list. With `enabled` false (a closed print popup) nothing is requested; turning it on
 * loads at once.
 */
export function useShipNodeStations(
  service: Pick<ShipNodeStationsService, 'listStations'>,
  intervalMs: number = STATION_REFRESH_MS,
  enabled = true,
) {
  const [state, setState] = useState<State>({
    stations: [],
    loading: true,
    error: null,
  });
  /* A slow response must not overwrite a newer one, or outlive the tab. */
  const latestRequest = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++latestRequest.current;
    try {
      const { stations } = await service.listStations();
      if (request !== latestRequest.current) return;
      setState({ stations: stations ?? [], loading: false, error: null });
    } catch (err) {
      if (request !== latestRequest.current) return;
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiErrorMessage(err, 'Could not load ShipNode stations'),
      }));
    }
  }, [service]);

  useEffect(() => {
    setState({ stations: [], loading: true, error: null });
    if (!enabled) return undefined;
    let timer: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      timer = setInterval(refresh, intervalMs);
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh();
        start();
      }
    };

    refresh();
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      latestRequest.current += 1;
    };
  }, [refresh, intervalMs, enabled]);

  return { ...state, refresh };
}
